import React, { useCallback, useEffect, useRef, useState } from "react";
import { C, font } from "../lib/styles.js";
import { buildFeedbackContext, pictureOnByDefault } from "../lib/feedback/context.js";
import { capturePage } from "../lib/feedback/screenshot.js";
import { feedbackApi, submitErrorText } from "../lib/feedback/feedbackCallables.js";
import { useModalDialog } from "../apps/ingest/views/rigv2/dialogFocus.js";

/**
 * The Feedback tab (Ivin, 2026-10-08): on every signed-in Suite page, a way
 * to report a bug, suggest something or ask a question. It notes where the
 * person is (the page and what's open on it), their device and browser, the
 * app version and recent errors, and, if ticked, a picture of the page, and
 * sends it to the admin inbox (Website > Feedback).
 *
 * Where it shows:
 *   desktop  a small tab on the right edge of the window (.feedback-edge-tab;
 *            under the rig screens' dialogs, which cover it)
 *   phones   no floating button over the page: "Send feedback" in the top
 *            bar's menu (AppTopbar) and a Feedback button on the home page's
 *            footer; both call openFeedback().
 * Not on the sign-in screens, the authenticator set-up, the second display
 * or for an event account (App.jsx does not mount it there).
 *
 * Everything this component draws carries data-feedback-ui, so the picture
 * leaves it out.
 */
export const FEEDBACK_OPEN_EVENT = "suite:feedback-open";
export const openFeedback = () => window.dispatchEvent(new CustomEvent(FEEDBACK_OPEN_EVENT));

export const TYPE_CHOICES = [
  { id: "bug", label: "Bug", placeholder: "What went wrong? What did you expect to happen?" },
  { id: "suggestion", label: "Suggestion", placeholder: "What would make this better?" },
  { id: "question", label: "Question", placeholder: "What would you like to know?" },
];
export const MESSAGE_MAX = 4000;
export const PICTURE_OFF_NOTE = {
  financials: "Off here because this page shows money. Tick it if a picture would help.",
  ingest: "Off here because this page can show guests' details. Tick it if a picture would help.",
  crm: "Off here because this page shows people's contact details. Tick it if a picture would help.",
  website: "Off here because this page shows prospects' details. Tick it if a picture would help.",
  jobs: "Off here because job pages show clients' and crew's details. Tick it if a picture would help.",
  sheets: "Off here because this page shows capture photos. Tick it if a picture would help.",
};
export const WHAT_IS_SENT = "Along with your note we send this page and what's open on it, your name, your device and browser, the app version and any recent errors. A picture never shows what's typed into text boxes or picked in dropdowns.";

export default function FeedbackTab({
  where, showEdgeTab = true, submit = feedbackApi.submit, capture = capturePage, account = null, currentAccount = null,
}) {
  const [open, setOpen] = useState(false);
  const show = useCallback(() => setOpen(true), []);
  // Stays on the page while the form is open (under the scrim, inert), so
  // focus can go back to it when the form closes.
  const tabRef = useRef(null);
  useEffect(() => {
    window.addEventListener(FEEDBACK_OPEN_EVENT, show);
    return () => window.removeEventListener(FEEDBACK_OPEN_EVENT, show);
  }, [show]);

  return (
    <>
      {showEdgeTab && (
        <button
          ref={tabRef}
          type="button"
          data-feedback-ui=""
          className="feedback-edge-tab"
          style={styles.edgeTab}
          onClick={show}
          aria-label="Send feedback"
          title="Report a bug, suggest something or ask a question"
        >
          <span style={styles.edgeTabText}>Feedback</span>
        </button>
      )}
      {open && (
        <FeedbackPanel where={where} onClose={() => setOpen(false)} submit={submit} capture={capture}
          account={account} currentAccount={currentAccount} returnFocusRef={tabRef} />
      )}
    </>
  );
}

export const PICTURE_FAILED_NOTE = "The picture of the page couldn't be made, so it went without one.";

/**
 * One send: the picture (the one taken as the form opened, else a new one)
 * if wanted, then the callable. A picture that fails never stops the send;
 * the report says it is missing and so does the thank-you. Resolves to
 * { pictureNote }; a refused or failed send throws (the form keeps the text).
 */
export const ACCOUNT_CHANGED = "account-changed";

export async function sendFeedback({ type, message, wantPicture, shot, capture, submit, where, win, stillValid = () => true }) {
  let pictureState = "unticked";
  let pictureData = null;
  if (wantPicture) {
    const result = await Promise.resolve(shot || capture()).catch(() => ({ ok: false }));
    if (result?.ok && typeof result.base64 === "string") { pictureState = "included"; pictureData = result.base64; } else pictureState = "failed";
  }
  // The form closed, or another account signed in (another tab), while the
  // picture was being made: nothing goes out under the wrong name.
  if (!stillValid()) throw Object.assign(new Error("The account changed; nothing was sent."), { code: ACCOUNT_CHANGED });
  const res = await submit({
    type,
    message,
    pictureState,
    ...(pictureData ? { picture: pictureData } : {}),
    context: buildFeedbackContext(where, win),
  });
  return { pictureNote: pictureState === "failed" || res?.picture === "rejected" ? PICTURE_FAILED_NOTE : "" };
}

export function FeedbackPanel({ where, onClose, submit, capture, account = null, currentAccount = null, returnFocusRef = null }) {
  const app = where?.app || null;
  const [type, setType] = useState("bug");
  const [message, setMessage] = useState("");
  const [picture, setPicture] = useState(() => pictureOnByDefault(app));
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null); // { pictureNote }
  const shot = useRef(null); // the picture taken when the form opened, if ticked then
  const textRef = useRef(null);
  const dialogRef = useRef(null);
  // A real modal: everything behind it inert (no Tab to the rig's Start
  // capture), focus kept inside, and back where it was on close.
  useModalDialog(dialogRef, { initialFocusRef: textRef, returnFocusRef });
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  // Who the form was opened for; a send checks it is still them.
  const openedFor = useRef(account);

  // Take the picture as the form opens (what the person was looking at),
  // only if the box starts ticked.
  // Once, as it opens: the app it opened on decides.
  const openedOn = useRef(app);
  useEffect(() => {
    if (pictureOnByDefault(openedOn.current)) shot.current = capture();
  }, [capture]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !sending) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, sending]);

  const trimmed = message.trim();
  const canSend = trimmed.length > 0 && trimmed.length <= MESSAGE_MAX && !sending;

  const send = async () => {
    if (!canSend) return;
    setSending(true);
    setError("");
    try {
      const stillValid = () => mounted.current && (!currentAccount || currentAccount() === openedFor.current);
      const result = await sendFeedback({ type, message: trimmed, wantPicture: picture, shot: shot.current, capture, submit, where, stillValid });
      if (mounted.current) setDone(result);
    } catch (e) {
      if (!mounted.current) return;
      setError(e?.code === ACCOUNT_CHANGED ? e.message : submitErrorText(e));
      setSending(false);
    }
  };

  const choice = TYPE_CHOICES.find((t) => t.id === type) || TYPE_CHOICES[0];

  return (
    <div data-feedback-ui="" className="feedback-scrim" style={styles.scrim} onMouseDown={(e) => { if (e.target === e.currentTarget && !sending) onClose(); }}>
      <div ref={dialogRef} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="feedback-title" className="feedback-panel" style={styles.panel}>
        {done ? (
          <div>
            <div id="feedback-title" style={styles.title}>Thanks, it's sent</div>
            <p style={styles.text}>Ivin will take a look. You can carry on where you were.</p>
            {done.pictureNote && <p style={{ ...styles.text, color: C.capture }}>{done.pictureNote}</p>}
            <div style={styles.actions}>
              <button type="button" style={styles.primary} onClick={onClose} autoFocus>Close</button>
            </div>
          </div>
        ) : (
          <>
            <div id="feedback-title" style={styles.title}>Send feedback</div>
            <p style={styles.text}>Found something wrong, or have an idea? Tell us here. We'll note which page you're on.</p>

            <div role="radiogroup" aria-label="Kind of feedback" style={styles.types}>
              {TYPE_CHOICES.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  role="radio"
                  aria-checked={type === t.id}
                  style={{ ...styles.typeBtn, ...(type === t.id ? styles.typeBtnOn : {}) }}
                  onClick={() => setType(t.id)}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <textarea
              ref={textRef}
              aria-label="Your feedback"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={choice.placeholder}
              maxLength={MESSAGE_MAX}
              rows={5}
              style={styles.textarea}
            />

            <label style={styles.check}>
              <input
                type="checkbox"
                checked={picture}
                onChange={(e) => { setPicture(e.target.checked); if (!e.target.checked) shot.current = null; }}
              />
              <span>Include a picture of this page</span>
            </label>
            {!pictureOnByDefault(app) && PICTURE_OFF_NOTE[app] && <p style={styles.hint}>{PICTURE_OFF_NOTE[app]}</p>}
            <p style={styles.hint}>{WHAT_IS_SENT}</p>

            {error && <p role="alert" style={styles.error}>{error}</p>}

            <div style={styles.actions}>
              <button type="button" style={styles.secondary} onClick={onClose} disabled={sending}>Cancel</button>
              <button type="button" style={{ ...styles.primary, ...(canSend ? {} : styles.disabled) }} onClick={send} disabled={!canSend}>
                {sending ? "Sending…" : "Send"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  edgeTab: {
    position: "fixed",
    right: 0,
    top: "50%",
    transform: "translateY(-50%)",
    zIndex: 40,
    // 22 px: narrower than the app's 24 px side padding, so at any desktop
    // width it sits in the margin, never over a page's own controls.
    width: 22,
    padding: "12px 0",
    background: C.surface,
    color: C.muted,
    border: `1px solid ${C.border}`,
    borderRight: "none",
    borderRadius: "6px 0 0 6px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    cursor: "pointer",
    fontFamily: font,
    alignItems: "center",
    justifyContent: "center",
  },
  edgeTabText: {
    writingMode: "vertical-rl",
    transform: "rotate(180deg)",
    fontSize: 11,
    fontWeight: 400,
    letterSpacing: "0.08em",
  },
  scrim: {
    position: "fixed",
    inset: 0,
    zIndex: 1000,
    background: "rgba(26,26,26,0.28)",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    padding: 16,
    fontFamily: font,
  },
  panel: {
    width: 380,
    maxWidth: "100%",
    maxHeight: "calc(100vh - 32px)",
    overflowY: "auto",
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 10,
    boxShadow: "0 14px 44px rgba(0,0,0,0.18)",
    padding: 20,
    boxSizing: "border-box",
    color: C.text,
  },
  title: { fontSize: 15, fontWeight: 500, marginBottom: 4 },
  text: { fontSize: 13, fontWeight: 300, color: C.muted, margin: "0 0 12px", lineHeight: 1.45 },
  types: { display: "flex", gap: 6, marginBottom: 10 },
  typeBtn: {
    flex: 1,
    padding: "8px 0",
    minHeight: 36,
    border: `1px solid ${C.border}`,
    borderRadius: 6,
    background: C.bg,
    color: C.muted,
    fontSize: 12.5,
    fontFamily: font,
    cursor: "pointer",
  },
  typeBtnOn: { background: C.text, color: "#FFF", borderColor: C.text },
  textarea: {
    width: "100%",
    boxSizing: "border-box",
    padding: "10px 12px",
    background: C.surfaceAlt,
    border: `1px solid ${C.border}`,
    borderRadius: 6,
    color: C.text,
    fontSize: 14,
    fontFamily: font,
    fontWeight: 300,
    resize: "vertical",
    outline: "none",
    minHeight: 110,
  },
  check: { display: "flex", alignItems: "center", gap: 8, fontSize: 13, marginTop: 10, cursor: "pointer", minHeight: 32 },
  hint: { fontSize: 11.5, color: C.muted, margin: "4px 0 0", lineHeight: 1.45 },
  error: { fontSize: 12.5, color: C.danger, margin: "10px 0 0" },
  actions: { display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 },
  primary: {
    padding: "8px 16px", minHeight: 36, background: C.text, color: "#FFF", border: "none", borderRadius: 6,
    fontSize: 13, fontFamily: font, cursor: "pointer",
  },
  secondary: {
    padding: "8px 14px", minHeight: 36, background: "transparent", color: C.muted, border: `1px solid ${C.border}`,
    borderRadius: 6, fontSize: 13, fontFamily: font, cursor: "pointer",
  },
  disabled: { opacity: 0.45, cursor: "default" },
};
